import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { ShieldAlert } from "lucide-react";

const AgeGateContext = createContext(null);
const KEY = "pv_age_verified";

export function AgeGateProvider({ children }) {
  const [verified, setVerified] = useState(() => localStorage.getItem(KEY) === "true");
  const [open, setOpen] = useState(false);

  const requestVerify = useCallback(() => {
    if (localStorage.getItem(KEY) === "true") {
      setVerified(true);
      return;
    }
    setOpen(true);
  }, []);

  const confirm = () => {
    localStorage.setItem(KEY, "true");
    setVerified(true);
    setOpen(false);
  };

  const deny = () => setOpen(false);

  const value = useMemo(() => ({ verified, requestVerify }), [verified, requestVerify]);

  return (
    <AgeGateContext.Provider value={value}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 backdrop-blur-sm p-4" data-testid="age-gate-modal">
          <div className="w-full max-w-md bg-surface border border-red-500/50 p-8 text-center">
            <ShieldAlert className="text-red-400 mx-auto mb-4" size={40} />
            <h2 className="font-display text-4xl text-paper">Age Verification</h2>
            <p className="font-mono text-xs text-muted-gray mt-3 leading-relaxed">
              This section contains adult (18+) content. Please confirm you are of legal age to view it.
            </p>
            <div className="mt-7 flex gap-3">
              <button onClick={deny} data-testid="age-gate-deny"
                className="flex-1 py-3.5 font-mono text-xs uppercase tracking-wider border border-stroke text-muted-gray hover:text-paper">
                I'm under 18
              </button>
              <button onClick={confirm} data-testid="age-gate-confirm"
                className="flex-1 py-3.5 font-mono text-xs uppercase tracking-wider bg-red-500 text-white font-bold hover:bg-red-600">
                I'm 18 or older
              </button>
            </div>
          </div>
        </div>
      )}
    </AgeGateContext.Provider>
  );
}

export const useAgeGate = () => useContext(AgeGateContext);
